/**
 * AppICEMAP — Directus SDK wrapper
 * Initializes the Directus SDK with configurable URL, auth token persistence,
 * and JWT refresh interceptor.
 */

import {
	createDirectus,
	rest,
	authentication,
	readItems,
	readItem,
	createItem,
	updateItem,
	deleteItem,
	uploadFiles,
	withToken,
	staticToken,
} from "@directus/sdk";

/**
 * Directus API base URL — resolved once here and imported everywhere else, so
 * there is a single place to look when requests go to the wrong host.
 *
 * Note it is baked in at BUILD time: changing .env requires a rebuild.
 */
export const DIRECTUS_URL = resolveApiBase();

function resolveApiBase() {
	const configured = (import.meta.env.VITE_DIRECTUS_URL || "").trim();
	const fallback = "https://feresdev-directusice-iedakg-886c4a-76-13-27-161.sslip.io";
	const base = (configured || fallback).replace(/\/+$/, "");

	// A relative or empty base makes the SDK post to the app's own origin, where
	// a static server answers with an HTML error page (typically nginx's
	// "405 Not Allowed") instead of JSON — a confusing failure worth naming.
	if (!/^https?:\/\//i.test(base)) {
		console.error(
			`[Directus] VITE_DIRECTUS_URL debe ser una URL absoluta (http:// o https://). Valor actual: "${base}". Las peticiones irán al propio origen de la app y fallarán.`,
		);
		return base;
	}

	console.info("[Directus] API base:", base);

	// Pointing the API at the app's own domain only works if a reverse proxy
	// forwards /auth, /items, /assets… to Directus. Without it the static server
	// answers POSTs with "405 Not Allowed" and nothing works, so say so loudly:
	// it is almost always VITE_DIRECTUS_URL set to the app URL by mistake.
	if (
		typeof window !== "undefined" &&
		new URL(base).origin === window.location.origin
	) {
		console.warn(
			`[Directus] La API apunta al mismo dominio que la app (${base}). Esto solo funciona si un proxy inverso reenvía las rutas de Directus; si no, los POST devolverán 405. Comprueba VITE_DIRECTUS_URL: debe ser la URL de tu instancia de Directus, no la de la app.`,
		);
	}

	return base;
}

/** @type {string | null} Static token for public reads (no auth) */
const PUBLIC_TOKEN = import.meta.env.VITE_DIRECTUS_PUBLIC_TOKEN || null;

/** localStorage key for user session */
const SESSION_KEY = "appicemap-session";

/* ── SDK Instance ─────────────────────────────────────────────────────────── */

/** @type {import('@directus/sdk').DirectusClient<any> & import('@directus/sdk').RestClient<any>} */
let client;

function createClient(token = null) {
	const options = {
		rest: {
			onRequest: (request) => {
				// You can add global request interceptors here
				return request;
			},
		},
	};

	const directus = createDirectus(DIRECTUS_URL, options).with(rest());

	if (token) {
		return directus.with(staticToken(token));
	}

	return directus;
}

/**
 * Get or initialize the Directus client.
 * If there's a stored session token, it will be attached.
 */
export function getClient() {
	if (!client) {
		const session = getSession();
		client = createClient(session?.access_token || PUBLIC_TOKEN);
	}
	return client;
}

/**
 * Re-initialize client with a new token (e.g., after login/logout).
 */
export function resetClient(token = null) {
	client = createClient(token);
}

/* ── Session Management ──────────────────────────────────────────────────── */

/**
 * Get stored session from localStorage.
 * @returns {{ access_token: string, refresh_token: string, expires_at: number } | null}
 */
export function getSession() {
	try {
		const raw = localStorage.getItem(SESSION_KEY);
		if (!raw) return null;
		const session = JSON.parse(raw);
		// Only drop the session once the REFRESH window is over. The short-lived
		// access token (~15 min) is renewed via the refresh token, so an expired
		// access token must NOT log the user out — otherwise the session appears
		// to "close by itself" a few minutes after login / on reload.
		if (
			session.refresh_expires_at &&
			Date.now() > session.refresh_expires_at
		) {
			localStorage.removeItem(SESSION_KEY);
			return null;
		}
		return session;
	} catch {
		return null;
	}
}

/**
 * Whether the stored access token is expired (or within `skewMs` of expiring).
 * @param {number} [skewMs] refresh this many ms before the real expiry
 * @returns {boolean}
 */
export function isAccessTokenExpired(skewMs = 60_000) {
	const session = getSession();
	if (!session || !session.expires_at) return false;
	return Date.now() > session.expires_at - skewMs;
}

/**
 * Store session in localStorage.
 */
export function setSession(session) {
	localStorage.setItem(SESSION_KEY, JSON.stringify(session));
	resetClient(session.access_token);
}

/**
 * Clear session (logout).
 */
export function clearSession() {
	localStorage.removeItem(SESSION_KEY);
	resetClient(PUBLIC_TOKEN);
}

/**
 * Check if user has a valid session.
 */
export function isAuthenticated() {
	return !!getSession();
}

/* ── Re-exports ──────────────────────────────────────────────────────────── */

export {
	readItems,
	readItem,
	createItem,
	updateItem,
	deleteItem,
	uploadFiles,
	withToken,
};

export default getClient;
